import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { classGroupKeys } from "@/modules/class-groups/queries/class-group.query";
import { studentService } from "../services/student.service";
import type {
	AssignClassRequest,
	BulkApproveStudentsRequest,
	CreateStudentRequest,
	ListStudentsParams,
	UpdateStudentRequest,
} from "../types";

// ─── Query Keys ─────────────────────────────────────────────────────
export const studentKeys = {
	all: ["students"] as const,
	lists: () => [...studentKeys.all, "list"] as const,
	list: (params: ListStudentsParams) => [...studentKeys.lists(), params] as const,
	details: () => [...studentKeys.all, "detail"] as const,
	detail: (id: string) => [...studentKeys.details(), id] as const,
};

// ─── useStudents (list) ─────────────────────────────────────────────
export function useStudents(params: ListStudentsParams) {
	return useQuery({
		queryKey: studentKeys.list(params),
		queryFn: () => studentService.list(params),
		enabled: !!params.organizationId,
	});
}

// ─── useStudent (getById) ───────────────────────────────────────────
export function useStudent(id: string) {
	return useQuery({
		queryKey: studentKeys.detail(id),
		queryFn: () => studentService.getById(id),
		enabled: !!id,
	});
}

// ─── useCreateStudent ───────────────────────────────────────────────
export function useCreateStudent() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateStudentRequest) => studentService.create(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: studentKeys.lists() });
			message.success("O'quvchi muvaffaqiyatli qo'shildi");
		},
	});
}

// ─── useUpdateStudent ───────────────────────────────────────────────
export function useUpdateStudent() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateStudentRequest }) =>
			studentService.update(id, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: studentKeys.lists() });
			queryClient.invalidateQueries({ queryKey: studentKeys.detail(variables.id) });
			message.success("O'quvchi muvaffaqiyatli yangilandi");
		},
	});
}

// ─── useDeleteStudent ───────────────────────────────────────────────
export function useDeleteStudent() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => studentService.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: studentKeys.lists() });
			message.success("O'quvchi muvaffaqiyatli o'chirildi");
		},
	});
}

// ─── useApproveStudent ──────────────────────────────────────────────
export function useApproveStudent() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => studentService.approve(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: studentKeys.lists() });
			message.success("O'quvchi muvaffaqiyatli tasdiqlandi");
		},
	});
}

// ─── useBulkApproveStudents ─────────────────────────────────────────
export function useBulkApproveStudents() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: BulkApproveStudentsRequest) => studentService.bulkApprove(data),
		onSuccess: (result) => {
			queryClient.invalidateQueries({ queryKey: studentKeys.lists() });
			if (result.approved > 0) {
				message.success(`${result.approved} ta o'quvchi tasdiqlandi`);
			} else {
				message.info("Hech qanday o'quvchi tasdiqlanmadi");
			}
			if (result.skippedNoClassGroup > 0) {
				message.warning(
					`${result.skippedNoClassGroup} ta o'quvchi sinfi bo'lmagani uchun o'tkazib yuborildi`
				);
			}
		},
	});
}

// ─── useAssignClass ─────────────────────────────────────────────────
export function useAssignClass() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: AssignClassRequest) => studentService.assignClass(data),
		onSuccess: (result, variables) => {
			// Sinf o'quvchilar soni ham o'zgaradi — class-group ro'yxatlari ham yangilanadi
			queryClient.invalidateQueries({ queryKey: studentKeys.lists() });
			queryClient.invalidateQueries({ queryKey: classGroupKeys.lists() });
			for (const id of variables.studentIds) {
				queryClient.invalidateQueries({ queryKey: studentKeys.detail(id) });
			}

			if (result.assigned > 0) {
				message.success(`${result.assigned} ta o'quvchi sinfga biriktirildi`);
			}

			const alreadyAssigned = result.skipped.filter((s) => s.reason === "already_assigned").length;
			if (alreadyAssigned > 0) {
				message.warning(`${alreadyAssigned} ta o'quvchi allaqachon sinfga biriktirilgan`);
			}

			const notFound = result.skipped.filter((s) => s.reason === "not_found").length;
			if (notFound > 0) {
				message.warning(`${notFound} ta o'quvchi topilmadi`);
			}
		},
	});
}
